import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserCheck, Users, User } from "lucide-react";
import { fetchUsers, getTeams } from "@/api/tenantDashboardService";
import scenarioService from "@/api/scenarioService";

interface ScenarioAssignModalProps {
    open: boolean;
    onClose: () => void;
    scenarioId: string;
    scenarioTitle: string;
    onSuccess: () => void;
}

export const ScenarioAssignModal: React.FC<ScenarioAssignModalProps> = ({ open, onClose, scenarioId, scenarioTitle, onSuccess }) => {
    const [users, setUsers] = useState<any[]>([]);
    const [teams, setTeams] = useState<any[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
    const [assignedTeamIds, setAssignedTeamIds] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const tenantId = localStorage.getItem("tenantId") || "";
            const [usersRes, teamsRes, assignmentsRes] = await Promise.all([
                fetchUsers({ page: 1, pageSize: 100, tenantId }), 
                getTeams(tenantId),
                scenarioService.getScenarioAssignments(scenarioId).catch(() => ({ userIds: [], teamIds: [] }))
            ]);

            const extractArray = (res: any) => {
                if (!res) return [];
                if (Array.isArray(res)) return res;
                if (Array.isArray(res.data)) return res.data;
                if (res.data && Array.isArray(res.data.data)) return res.data.data;
                if (res.data && Array.isArray(res.data.items)) return res.data.items;
                if (res.items && Array.isArray(res.items)) return res.items;
                return [];
            };

            setUsers(extractArray(usersRes));
            setTeams(extractArray(teamsRes));
            setAssignedUserIds(assignmentsRes.userIds || []);
            setAssignedTeamIds(assignmentsRes.teamIds || []);
        } catch (error) {
            console.error("Failed to fetch users/teams", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAssign = async () => {
        setSubmitting(true);
        setErrorMsg(null);
        try {
            await scenarioService.assignScenario(scenarioId, {
                userIds: selectedUserIds,
                teamIds: selectedTeamIds
            });
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("Failed to assign scenario", error);
            setErrorMsg(error?.response?.data?.message || "Failed to assign scenario");
        } finally {
            setSubmitting(false);
        }
    };

    const toggleUser = (id: string) => {
        setSelectedUserIds(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
    };

    const toggleTeam = (id: string) => {
        setSelectedTeamIds(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
    };

    if (!open) return null;

    const CustomCheckbox = ({ checked, onChange, label, icon: Icon, disabled, alreadyAssigned }: any) => (
        <label className={`flex items-center gap-3 p-3 rounded-2xl border ${alreadyAssigned ? 'border-white/[0.02] bg-white/[0.01] opacity-60 cursor-not-allowed' : 'border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.05] cursor-pointer'} transition-colors group`}>
            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${alreadyAssigned ? 'bg-white/10 border-transparent' : checked ? 'bg-[#B8F890] border-[#B8F890]' : 'border-white/[0.2] group-hover:border-white/[0.4]'}`}>
                {(checked || alreadyAssigned) && <svg className={`w-3.5 h-3.5 ${alreadyAssigned ? 'text-white/40' : 'text-black'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
            </div>
            <input type="checkbox" className="hidden" checked={checked || alreadyAssigned} onChange={onChange} disabled={disabled || alreadyAssigned} />
            <div className="flex items-center gap-2 flex-1 min-w-0">
                {Icon && <Icon className="w-4 h-4 text-white/80 shrink-0" />}
                <span className={`text-sm font-bold truncate ${checked || alreadyAssigned ? 'text-white' : 'text-white/80'}`}>{label}</span>
                {alreadyAssigned && <span className="ml-auto text-[10px] uppercase font-bold text-white/40 bg-white/5 px-2 py-0.5 rounded-md shrink-0">Assigned</span>}
            </div>
        </label>
    );

    return (
        <AnimatePresence>
            <div 
                className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md" 
                onClick={onClose}
            >
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-[#0f0f0f] border border-white/[0.08] rounded-[2.5rem] w-full max-w-xl p-10 shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]" 
                    onClick={e => e.stopPropagation()}
                >
                    <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                        <UserCheck className="w-32 h-32 text-[#B8F890]" />
                    </div>

                    <div className="flex items-start justify-between relative z-10 shrink-0 mb-8">
                        <div className="space-y-2 pr-8">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#B8F890] font-bold flex items-center gap-2">
                                <UserCheck className="w-3.5 h-3.5" />
                                Assign Scenario
                            </p>
                            <h2 className="text-white font-bold text-2xl leading-tight">{scenarioTitle}</h2>
                        </div>
                        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all shrink-0">
                            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {loading ? (
                        <div className="py-20 flex flex-col items-center justify-center gap-4 relative z-10 flex-1">
                            <div className="w-8 h-8 border-2 border-white/10 border-t-[#B8F890] rounded-full animate-spin" />
                            <p className="text-white font-bold animate-pulse">Loading members...</p>
                        </div>
                    ) : (
                        <div className="space-y-8 relative z-10 overflow-y-auto flex-1 pr-2 custom-scrollbar">
                            <div className="space-y-4">
                                <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold border-b border-white/[0.05] pb-2">Select Teams</p>
                                <div className="grid grid-cols-2 gap-3">
                                    {teams.map((t: any) => (
                                        <CustomCheckbox
                                            key={t.id}
                                            checked={selectedTeamIds.includes(t.id)}
                                            onChange={() => toggleTeam(t.id)}
                                            label={t.name}
                                            icon={Users}
                                            alreadyAssigned={assignedTeamIds.includes(t.id)}
                                        />
                                    ))}
                                    {teams.length === 0 && <p className="text-white text-sm font-medium col-span-2">No teams found.</p>}
                                </div>
                            </div>

                            <div className="space-y-4">
                                <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold border-b border-white/[0.05] pb-2">Select Users</p>
                                <div className="grid grid-cols-2 gap-3">
                                    {users.map((u: any) => (
                                        <CustomCheckbox
                                            key={u.id}
                                            checked={selectedUserIds.includes(u.id)}
                                            onChange={() => toggleUser(u.id)}
                                            label={u.name || u.email}
                                            icon={User}
                                            alreadyAssigned={assignedUserIds.includes(u.id)}
                                        />
                                    ))}
                                    {users.length === 0 && <p className="text-white text-sm font-medium col-span-2">No users found.</p>}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="pt-8 mt-4 border-t border-white/[0.05] flex flex-col gap-3 relative z-10 shrink-0">
                        {errorMsg && (
                            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium text-center">
                                {errorMsg}
                            </div>
                        )}
                        <div className="flex gap-3">
                            <button 
                                onClick={onClose}
                            disabled={submitting}
                            className="flex-1 bg-white/5 hover:bg-white/10 text-white hover:text-white font-bold py-3.5 rounded-2xl transition-all uppercase tracking-widest text-[11px] disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleAssign}
                            disabled={submitting || (selectedUserIds.length === 0 && selectedTeamIds.length === 0)}
                            className="flex-1 bg-[#B8F890] hover:bg-[#a0e070] text-black font-bold py-3.5 rounded-2xl transition-all uppercase tracking-widest text-[11px] disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(184,248,144,0.15)] flex justify-center items-center gap-2"
                        >
                            {submitting && <div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />}
                            {submitting ? "Assigning..." : "Confirm Assign"}
                        </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
